/**
 * Things users do that can move a journey forward.
 *
 * Product code reports what happened with `emit(name, payload)` and never
 * knows which journey (if any) is listening. Every payload carries `boardId`
 * so a journey started on one board ignores activity on another.
 *
 * `id` names the payload field that identifies the thing the event is about.
 * Journeys use it with `fresh` to ignore things that already existed before
 * the current step began (a script card rendered from chat history, say).
 */

export interface CoachEventDefinition {
  label: string;
  payload: readonly string[];
  id?: string;
}

export const COACH_EVENTS = {
  'discover.opened': { label: 'Opened Discover', payload: ['boardId'] },
  'discover.selected': { label: 'Selected an example in Discover', payload: ['boardId'] },
  'discover.cleared': { label: 'Cleared the Discover selection', payload: ['boardId'] },
  'discover.added': {
    label: 'Saved a Discover example to Inspiration',
    payload: ['boardId', 'inspirationGroupId', 'nodeId'],
    id: 'nodeId',
  },
  'copilot.research-ready': {
    label: 'Real Discover results are ready',
    payload: ['boardId', 'turnId', 'toolCallId'],
    id: 'toolCallId',
  },
  'copilot.requested': { label: 'A Copilot turn was submitted', payload: ['boardId', 'turnId'] },
  'copilot.ideas-ready': {
    label: 'Ten validated ideas are ready to choose',
    payload: ['boardId', 'ideas', 'toolCallId'],
    id: 'toolCallId',
  },
  'board.entered': { label: 'Opened a board they can edit', payload: ['boardId'] },
  'copilot.sent': { label: 'Sent Copilot a message', payload: ['boardId'] },
  'copilot.replied': { label: 'Copilot finished a reply', payload: ['boardId'] },
  // A word the Copilot was told to put at the end of a reply when a part of a guided
  // conversation is done (`[[coach:review]]`): the app strips it from what the person reads
  // and reports it here, so a journey led by the chat knows when to move on.
  'copilot.signal': { label: 'Copilot signalled the journey', payload: ['boardId', 'signal'] },
  'copilot.failed': { label: 'Copilot could not finish a reply', payload: ['boardId', 'message'] },
  'project.updated': {
    label: 'Copilot saved changes to a Content Project',
    payload: ['boardId', 'projectNodeId', 'toolCallId'],
    id: 'toolCallId',
  },
  'chat.sent': { label: 'Sent a chat message', payload: ['boardId', 'chatNodeId'] },
  'chat.replied': { label: 'A board chat finished a reply', payload: ['boardId', 'chatNodeId'] },
  // A state, not a moment: reported for each node a journey keeps in its context (a link it
  // added, say) whose content is read (a video transcribed, a site scraped) so a chat can use
  // it, and again whenever the step changes, so a step that begins after the node finished
  // still hears it.
  'node.ready': { label: 'A node finished processing', payload: ['boardId', 'nodeId'] },
  'node.failed': { label: 'A node could not be processed', payload: ['boardId', 'nodeId'] },
  // Anything new on the board (a recording, an upload, a note), with its type: a step can
  // take what the person adds through the board's own toolbar as their answer.
  // `parentId` is the group it landed in, or '' on its own: a step can take only what goes
  // into the group it is about (`matchAs: { parentId: 'businessGroupId' }`).
  'node.added': {
    label: 'Something was added to the board',
    payload: ['boardId', 'nodeId', 'nodeType', 'parentId'],
    id: 'nodeId',
  },
  // A carousel a board chat started making (it lands at once and fills in over minutes).
  'carousel.created': {
    label: 'A chat made a carousel',
    payload: ['boardId', 'chatNodeId', 'nodeId'],
    id: 'nodeId',
  },
  'chat.script-ready': {
    label: 'A script card appeared in chat',
    payload: ['boardId', 'chatNodeId', 'toolCallId'],
    id: 'toolCallId',
  },
  // Reported by Content Studio's own `project_created` milestone, so every way of making a
  // project counts: Copilot, a chat's script card, the toolbar, Content Studio, Trend Radar.
  'project.created': {
    label: 'Added a Content Project to the board',
    payload: ['boardId', 'projectNodeId', 'source'],
    id: 'projectNodeId',
  },
  'studio.opened': { label: 'Opened the studio', payload: ['boardId', 'projectNodeId'] },
  'studio.camera-chosen': {
    label: 'Chose a camera in the studio',
    payload: ['boardId', 'projectNodeId'],
  },
  'studio.upload-chosen': {
    label: 'Chose to upload a video in the studio',
    payload: ['boardId', 'projectNodeId'],
  },
  'studio.editing-done': {
    label: 'Finished editing the video and went back to the post',
    payload: ['boardId', 'projectNodeId'],
  },
  'project.take-saved': {
    label: 'A take was saved to the project',
    payload: ['boardId', 'projectNodeId'],
  },
  'post.opened': { label: 'Opened the posting window', payload: ['boardId', 'projectNodeId'] },
  'project.planned': {
    label: 'Saved a Content Project plan to the calendar',
    payload: ['boardId', 'projectNodeId'],
  },
  'post.accepted': {
    label: 'Every requested channel accepted the post',
    payload: ['boardId', 'projectNodeId', 'outcome'],
  },
  'post.closed': { label: 'Closed the posting window', payload: ['boardId', 'projectNodeId'] },
  'post.sent': {
    label: 'A project was accepted for posting or scheduling',
    payload: ['boardId', 'projectNodeId'],
  },
} as const satisfies Record<string, CoachEventDefinition>;

export type CoachEventName = keyof typeof COACH_EVENTS;

export const COACH_EVENT_NAMES = Object.keys(COACH_EVENTS) as CoachEventName[];

type PayloadKey<E extends CoachEventName> = (typeof COACH_EVENTS)[E]['payload'][number];

export type CoachEventPayload<E extends CoachEventName> = { [K in PayloadKey<E>]: string };

export type CoachEvent = {
  [E in CoachEventName]: { name: E; payload: CoachEventPayload<E> };
}[CoachEventName];
